import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { TicketCard } from '@/components/cards/TicketCard';
import { OpportunityCard as OppCard } from '@/components/cards/OpportunityCard';
import { MultiSelect } from '@/components/MultiSelect';
import { PriorityTag } from '@/components/PriorityTag';
import { AssignedResourcePicker } from '@/components/AssignedResourcePicker';
import { InlineEdit } from '@/components/InlineEdit';
import {
  useTickets, useOpportunities, usePicklists, useOppPicklists,
  useResources, useCompanyLookup, useMe, useUpdateOpportunity,
  type TicketFilters, type OppFilters,
} from '@/api/hooks';
import { dueState, fmtCurrency, fmtDate, fmtRelative, stageColor } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';
import { ticketPath, oppPath } from '@/lib/routes';

const SUPPORT_QUEUE_ID = 29682833;
const AUTOTASK_TICKET_URL = 'https://ww31.autotask.net/Mvc/ServiceDesk/TicketDetail.mvc?ticketId=';
const AUTOTASK_OPP_URL   = 'https://ww31.autotask.net/Mvc/CRM/OpportunityDetail.mvc?opportunityID=';

const EMPTY_TICKET_FILTERS: TicketFilters = { assignedTo: 'any', status: [], excludeStatus: [], queueID: [], priority: [], search: '' };
const EMPTY_OPP_FILTERS: OppFilters       = { assignedTo: 'any', stage: [], search: '' };

// Autotask returns one page; the server caps at 500. Hitting it means truncated results.
const OPP_PAGE_CAP = 500;

const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

type ActiveTab = 'tickets' | 'opps';

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-md py-2 text-sm font-medium transition-colors',
        active ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ── Ticket search ──────────────────────────────────────────────────────────────

function TicketSearch() {
  const [text, setText]             = useState('');
  const [company, setCompany]       = useState<CompanyOption | null>(null);
  const [resourceID, setResourceID] = useState('');
  const [queueID, setQueueID]       = useState(String(SUPPORT_QUEUE_ID));
  const [createdAfter, setCreatedAfter] = useState('');
  const [openOnly, setOpenOnly]     = useState(true);
  const [submitted, setSubmitted]   = useState<TicketFilters | null>(null);

  const picklists = usePicklists();
  const resources = useResources();
  const tickets   = useTickets(submitted ?? EMPTY_TICKET_FILTERS, { enabled: submitted !== null, scoped: true });

  const items = tickets.data?.items ?? [];
  const companyIds = useMemo(() => [...new Set(items.map((t) => t.companyID).filter((v): v is number => v != null))], [items]);
  const companies  = useCompanyLookup(companyIds);

  const companyMap  = useMemo(() => { const m = new Map<number, string>(); for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName); return m; }, [companies.data]);
  const pl = picklists.data;
  const statusMap   = useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.status   ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);
  const priorityMap = useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.priority ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);
  const queueMap    = useMemo(() => { const m = new Map<number, string>(); for (const o of pl?.queueID  ?? []) m.set(Number(o.value), o.label); return m; }, [pl]);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSubmitted({
      assignedTo: 'any', search: text.trim(), status: [],
      excludeStatus: openOnly ? [5, 21] : [],
      queueID: queueID ? [Number(queueID)] : [],
      priority: [],
      companyID: company?.id,
      resourceID: resourceID ? Number(resourceID) : undefined,
      createdAfter: createdAfter ? new Date(createdAfter).toISOString() : undefined,
    });
  };

  const clearSearch = () => { setText(''); setCompany(null); setResourceID(''); setQueueID(String(SUPPORT_QUEUE_ID)); setCreatedAfter(''); setOpenOnly(true); setSubmitted(null); };

  return (
    <div className="space-y-3">
      <form onSubmit={handleSearch} className="space-y-3 rounded-lg border border-border bg-card p-3 shadow-sm">
        <SearchInput value={text} onValueChange={setText} placeholder="Title, description or ticket #…" />
        <CompanySearch value={company} onChange={setCompany} placeholder="Any company…" />
        <select value={resourceID} onChange={(e) => setResourceID(e.target.value)} className={SELECT_CLS}>
          <option value="">Any assignee</option>
          {(resources.data?.items ?? []).map((r) => <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>)}
        </select>
        <select value={queueID} onChange={(e) => setQueueID(e.target.value)} className={SELECT_CLS}>
          <option value="">Any queue</option>
          {(pl?.queueID ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Created after</label>
          <DatePicker size="md" className="w-full" value={createdAfter || null} onChange={(v) => setCreatedAfter(v ?? '')} placeholder="Any date" />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} className="h-3.5 w-3.5 rounded border-input accent-primary" />
          Open tickets only
        </label>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1"><Search className="mr-1.5 h-3.5 w-3.5" />Search</Button>
          {submitted && <Button type="button" variant="ghost" size="icon" onClick={clearSearch}><X className="h-4 w-4" /></Button>}
        </div>
      </form>

      {!submitted && <p className="py-6 text-center text-sm text-muted-foreground">Set filters above and tap Search.</p>}
      {submitted && tickets.isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Searching…</p>}
      {submitted && !tickets.isLoading && items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No tickets found.</p>}
      {submitted && items.length > 0 && (
        <>
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">{items.length} result{items.length !== 1 ? 's' : ''}</p>
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {items.map((t) => <TicketCard key={t.id} t={t} companyMap={companyMap} statusMap={statusMap} priorityMap={priorityMap} queueMap={queueMap} />)}
          </div>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">#</th><th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Company</th><th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Priority</th><th className="px-3 py-2">Assignee</th>
                  <th className="px-3 py-2">Activity</th><th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {items.map((t) => {
                  const pl2 = picklists.data;
                  return (
                    <tr key={t.id} className={cn('group hover:bg-muted/30', dueState(t.dueDateTime) === 'overdue' && 'rail-danger')}>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground"><Link to={ticketPath(t.id)} className="hover:underline">{t.ticketNumber}</Link></td>
                      <td className="max-w-xs px-3 py-2"><Link to={ticketPath(t.id)} className="line-clamp-2 text-sm font-medium hover:text-brand">{t.title}</Link></td>
                      <td className="max-w-[140px] truncate px-3 py-2 text-xs text-muted-foreground">{t.companyID != null ? companyMap.get(t.companyID) ?? '' : ''}</td>
                      <td className="px-3 py-2"><InlineEdit ticketId={t.id} field="status" value={t.status} options={pl2?.status ?? []} variant="text" /></td>
                      <td className="px-3 py-2">{priorityMap.get(t.priority) && <PriorityTag priority={priorityMap.get(t.priority)!} />}</td>
                      <td className="px-3 py-2"><AssignedResourcePicker ticketId={t.id} value={t.assignedResourceID ?? null} /></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{t.lastActivityDate ? fmtRelative(t.lastActivityDate) : '—'}</td>
                      <td className="px-3 py-2"><a href={`${AUTOTASK_TICKET_URL}${t.id}`} target="_blank" rel="noreferrer" className="opacity-0 group-hover:opacity-60 hover:!opacity-100"><ExternalLink className="h-3.5 w-3.5" /></a></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Opportunity search ─────────────────────────────────────────────────────────

function OppSearch() {
  const [text, setText]                     = useState('');
  const [company, setCompany]               = useState<CompanyOption | null>(null);
  const [ownerResourceID, setOwnerResourceID] = useState('');
  const [stageSel, setStageSel]             = useState<number[]>([]);
  const [statusSel, setStatusSel]           = useState<number[]>([]);
  const [createdAfter, setCreatedAfter]     = useState('');
  const [submitted, setSubmitted]           = useState<OppFilters | null>(null);

  const picklists = useOppPicklists();
  const resources = useResources();
  const me        = useMe();
  const updateOpp = useUpdateOpportunity();
  const opps      = useOpportunities(submitted ?? EMPTY_OPP_FILTERS, { enabled: submitted !== null, scoped: true });

  const items = opps.data?.items ?? [];
  const myResourceId = me.data?.item?.id;
  const companyIds = useMemo(() => [...new Set(items.map((o) => o.companyID).filter((v): v is number => v != null))], [items]);
  const companies  = useCompanyLookup(companyIds);

  const companyMap  = useMemo(() => { const m = new Map<number, string>(); for (const c of companies.data?.items ?? []) m.set(c.id, c.companyName); return m; }, [companies.data]);
  const resourceMap = useMemo(() => { const m = new Map<number, string>(); for (const r of resources.data?.items ?? []) m.set(r.id, `${r.firstName} ${r.lastName}`.trim()); return m; }, [resources.data]);
  const pl = picklists.data;
  const stageMap  = useMemo(() => { const m = new Map<string, string>(); for (const o of pl?.stage ?? []) m.set(o.value, o.label); return m; }, [pl]);
  const statusMap = useMemo(() => { const m = new Map<string, string>(); for (const o of pl?.status ?? []) m.set(o.value, o.label); return m; }, [pl]);

  // Most recent activity first, matching the desktop Opportunity Search page.
  const sortedItems = useMemo(
    () => [...items].sort((a, b) =>
      (b.lastActivity ? new Date(b.lastActivity).getTime() : 0) - (a.lastActivity ? new Date(a.lastActivity).getTime() : 0)),
    [items],
  );

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setSubmitted({
      assignedTo: 'any', search: text.trim(),
      stage: stageSel,
      // No status picked → search all statuses (includeAll); pick any to narrow.
      status: statusSel.length ? statusSel : undefined,
      includeAll: statusSel.length === 0,
      companyID: company?.id,
      ownerResourceID: ownerResourceID ? Number(ownerResourceID) : undefined,
      createdAfter: createdAfter ? new Date(createdAfter).toISOString() : undefined,
    });
  };

  const clearSearch = () => { setText(''); setCompany(null); setOwnerResourceID(''); setStageSel([]); setStatusSel([]); setCreatedAfter(''); setSubmitted(null); };

  return (
    <div className="space-y-3">
      <form onSubmit={handleSearch} className="space-y-3 rounded-lg border border-border bg-card p-3 shadow-sm">
        <SearchInput value={text} onValueChange={setText} placeholder="Title or description…" />
        <CompanySearch value={company} onChange={setCompany} placeholder="Any company…" />
        <select value={ownerResourceID} onChange={(e) => setOwnerResourceID(e.target.value)} className={SELECT_CLS}>
          <option value="">Any owner</option>
          {(resources.data?.items ?? []).map((r) => <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          <MultiSelect label="Any status" options={pl?.status ?? []} selected={statusSel} onChange={setStatusSel} />
          <MultiSelect label="Any stage" options={pl?.stage ?? []} selected={stageSel} onChange={setStageSel} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Created after</label>
          <DatePicker size="md" className="w-full" value={createdAfter || null} onChange={(v) => setCreatedAfter(v ?? '')} placeholder="Any date" />
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="flex-1"><Search className="mr-1.5 h-3.5 w-3.5" />Search</Button>
          {submitted && <Button type="button" variant="ghost" size="icon" onClick={clearSearch}><X className="h-4 w-4" /></Button>}
        </div>
      </form>

      {!submitted && <p className="py-6 text-center text-sm text-muted-foreground">Set filters above and tap Search.</p>}
      {submitted && opps.isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Searching…</p>}
      {submitted && !opps.isLoading && items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No opportunities found.</p>}
      {submitted && items.length > 0 && (
        <>
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">{items.length} result{items.length !== 1 ? 's' : ''}</p>
          {items.length >= OPP_PAGE_CAP && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              Showing the first {OPP_PAGE_CAP} — narrow your filters to see the rest.
            </div>
          )}
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {sortedItems.map((o) => (
              <OppCard key={o.id} o={o} companyMap={companyMap} stageMap={stageMap} stageOpts={pl?.stage ?? []}
                statusOpts={pl?.status ?? []}
                resourceMap={resourceMap} myResourceId={myResourceId}
                onStageChange={(id, stageId) => updateOpp.mutate({ id, fields: { stage: stageId } })}
                onStatusChange={(id, status) => updateOpp.mutate({ id, fields: { status } })}
                pendingStage={updateOpp.isPending && updateOpp.variables?.fields?.stage !== undefined && updateOpp.variables?.id === o.id}
                pendingStatus={updateOpp.isPending && updateOpp.variables?.fields?.status !== undefined && updateOpp.variables?.id === o.id} />
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">#</th><th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Company</th><th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Amount</th><th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Close</th><th className="px-3 py-2">Last Activity</th><th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {sortedItems.map((o) => {
                  const stageLabel = o.stage != null ? stageMap.get(String(o.stage)) ?? null : null;
                  const statusLabel = o.status != null ? statusMap.get(String(o.status)) ?? null : null;
                  return (
                    <tr key={o.id} className="group hover:bg-muted/30">
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground"><Link to={oppPath(o.id)} className="hover:underline">#{o.id}</Link></td>
                      <td className="max-w-xs px-3 py-2"><Link to={oppPath(o.id)} className="line-clamp-2 text-sm font-medium hover:text-brand">{o.title}</Link></td>
                      <td className="max-w-[140px] truncate px-3 py-2 text-xs text-muted-foreground">{o.companyID != null ? companyMap.get(o.companyID) ?? '' : ''}</td>
                      <td className="px-3 py-2">
                        {stageLabel && <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: `color-mix(in oklab, ${stageColor(stageLabel)} 15%, transparent)`, color: stageColor(stageLabel) }}>{stageLabel}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{statusLabel ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs tabular-nums">{o.amount != null ? fmtCurrency(o.amount) : '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{o.ownerResourceID != null ? resourceMap.get(o.ownerResourceID) ?? '—' : '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{o.projectedCloseDate ? fmtDate(o.projectedCloseDate) : '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{o.lastActivity ? fmtDate(o.lastActivity) : '—'}</td>
                      <td className="px-3 py-2"><a href={`${AUTOTASK_OPP_URL}${o.id}`} target="_blank" rel="noreferrer" className="opacity-0 group-hover:opacity-60 hover:!opacity-100"><ExternalLink className="h-3.5 w-3.5" /></a></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Combined page ──────────────────────────────────────────────────────────────

export default function SearchPage() {
  const [tab, setTab] = useState<ActiveTab>('tickets');

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4">
      <h1 className="mb-4 text-[22px] font-[650] tracking-[-0.018em]">Search</h1>

      {/* Tab switcher */}
      <div className="mb-4 flex gap-1 rounded-lg bg-muted p-1">
        <TabButton active={tab === 'tickets'} onClick={() => setTab('tickets')}>Tickets</TabButton>
        <TabButton active={tab === 'opps'}    onClick={() => setTab('opps')}>Opportunities</TabButton>
      </div>

      {tab === 'tickets' ? <TicketSearch /> : <OppSearch />}
    </div>
  );
}
